import { useMemo } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";

/**
 * The tags anyone has used on an event, from `GET tag/?scope=event`.
 *
 * ## One shared vocabulary, scoped per tag
 *
 * The `Tag` collection is not the event's own list: notices and groups draw on the same
 * one, and each tag carries the scopes it is offered under. That is what makes
 * "everything tagged Boarders" answerable across domains, and it is why this asks for a
 * scope rather than for a document named after this app.
 *
 * It moved here from `Dictionary/EventTags`, a single document holding an array. Two
 * things forced the move: an array cannot carry a unique index, so one spelling per tag
 * was application code in three places rather than a guarantee; and `dictionary/{name}`
 * has NO authentication at all, which was survivable for "Sport" and is not once other
 * domains put their vocabulary in the same list. This endpoint is staff-only.
 *
 * ## This catalogue is a SUGGESTION LIST, not a closed vocabulary
 *
 * Unlike `useEventTypes` — where the API routes approvals on the value and
 * rejects anything outside the dictionary with a 422 — nothing here is
 * enforced. The editor's field accepts any tag, and the API adds one it has
 * never seen to the vocabulary as the event saves. So this is the accumulated
 * record of what people have already typed, and its only job is to make the
 * next person pick the existing spelling instead of coining a second.
 *
 * That difference decides how the failure cases read. An empty
 * `EventActivityTypes` means an unseeded environment and callers are told to
 * say so; an empty tag list means a school that has not tagged anything yet,
 * which needs no warning.
 *
 * ## Order, and why it is not alphabetical
 *
 * The API answers most-used first, then alphabetical. That ordering is the whole
 * mechanism by which an open vocabulary converges: the tag most people have already
 * chosen is the one the next person most likely wants. Do NOT re-sort this.
 *
 * ## Case
 *
 * Still deduplicated here, defensively. The unique index on the collection now makes two
 * spellings of one tag impossible at the source, so this should never fire — but it costs
 * one pass over a short list, and a picker showing "Sport" twice is a worse failure than
 * a redundant guard.
 */

export interface EventTagCatalogue {
	/** Distinct tags, alphabetical. */
	tags: string[];
	loaded: boolean;
	failed: boolean;
}

/** Staff-only, and scoped: this app only ever wants the event vocabulary. */
const ENDPOINT = "tag/?scope=event";

const EMPTY: string[] = [];

/**
 * One row's tag name.
 *
 * The endpoint answers `{Name, Scopes, UsageCount}` objects. Bare strings are still
 * accepted because the vocabulary spent a while in the Dictionary collection, which is
 * not consistent about it — TodoTips holds plain strings — and a hand-seeded list in the
 * simpler style should work rather than silently read as empty.
 */
function nameOf(entry: unknown): string {
	if (typeof entry === "string") return entry.trim();
	if (entry && typeof entry === "object") {
		const row = entry as { Name?: unknown; value?: unknown };
		const name = row.Name ?? row.value;
		if (typeof name === "string") return name.trim();
	}
	return "";
}

export function useEventTags(): EventTagCatalogue {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	const query = useQuery<string[]>({
		queryKey: ["tags", "event"],
		queryFn: async () => {
			const res = await callPymbleApi(instance, "GET", ENDPOINT);
			/* An empty list is nobody having coined a tag yet, not an error. */
			return Array.isArray(res) ? (res as unknown[]).map(nameOf) : [];
		},
		/* The school's own vocabulary. It grows when somebody saves an event with
		   a new tag, which is rare enough that refetching per mount is noise —
		   and the editor adds a freshly coined tag to its own list locally, so
		   the person who coined it never waits on this. */
		staleTime: 60 * 60 * 1000,
		enabled: isAuthenticated,
	});

	const tags = useMemo(() => {
		const rows = query.data ?? EMPTY;
		const seen = new Set<string>();
		const out: string[] = [];
		for (const tag of rows) {
			if (!tag) continue;
			const key = tag.toLocaleLowerCase("en-AU");
			if (seen.has(key)) continue;
			seen.add(key);
			out.push(tag);
		}
		/* NOT re-sorted. The API returns most-used first, then alphabetical, and that
		   ordering is what makes the picker converge on the tag people already use.
		   Sorting alphabetically here would quietly throw it away. */
		return out;
	}, [query.data]);

	return {
		tags,
		loaded: !query.isPending,
		failed: query.isError,
	};
}
